import {
  INVESTMENT_EXPERIENCE_VALUES,
  type AccountDocument,
  type FuturesAgreementRecord,
} from "@tastyworks/tastyworks-api"
import { boolean, type InferType, object, string } from "yup"
import { INVESTMENT_EXPERIENCES } from "@tastyworks/shared-utils/misc/constants"
import type { FormDataAdapter } from "$lib/util"

export const futuresTradingAddAccountSchema = object({
  addFuturesAccount: boolean().required(),
})

export const futuresTradingExperienceSchema = object({
  futuresTradingExperience: string<INVESTMENT_EXPERIENCES>()
    .oneOf(INVESTMENT_EXPERIENCE_VALUES)
    .required(),
})

export const CFTC_AFFILIATION_TYPES = [
  "regulatory_body_or_exchange_member",
  "exemption",
  "fcm_or_ib",
] as const

export const [REGULATORY_BODY_OR_EXCHANGE_MEMBER, EXEMPTION, FCM_OR_IB] =
  CFTC_AFFILIATION_TYPES

export type CftcAffiliationType = (typeof CFTC_AFFILIATION_TYPES)[number]

export const futuresTradingRegulatoryFormSchema = object({
  hasCftcAffiliation: boolean().required(),
  cftcAffiliationType: string<CftcAffiliationType | "">()
    .oneOf([...CFTC_AFFILIATION_TYPES, ""])
    .default("")
    .when("hasCftcAffiliation", {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  affiliationDetail: string()
    .default("")
    .when(["hasCftcAffiliation", "cftcAffiliationType"], {
      is: (hasCftcAffiliation: boolean, cftcAffiliationType: string) =>
        hasCftcAffiliation &&
        (cftcAffiliationType === REGULATORY_BODY_OR_EXCHANGE_MEMBER ||
          cftcAffiliationType === EXEMPTION),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
})

export const futuresTradingDocumentsSchema = object({
  agreedToDocuments: boolean().required().oneOf([true]),
})

export const futuresTradingSchema = object({
  futuresTradingAddAccount: futuresTradingAddAccountSchema,
  futuresTradingExperience: futuresTradingExperienceSchema,
  futuresTradingRegulatory: futuresTradingRegulatoryFormSchema,
  futuresTradingDocuments: futuresTradingDocumentsSchema,
})

const FUTURES_AGREEMENT_SLUGS = new Set([
  "broker_futures_customer_agreement",
  "clearing_futures_exchange_traded_options_customer_agreement",
])

export function partitionFuturesDocuments(documents: AccountDocument[]) {
  const agreements: AccountDocument[] = []
  const disclosures: AccountDocument[] = []

  for (const document of documents) {
    if (FUTURES_AGREEMENT_SLUGS.has(document.slug)) {
      agreements.push(document)
    } else {
      disclosures.push(document)
    }
  }

  return { agreements, disclosures }
}

export const futuresAgreementAdapter: FormDataAdapter<
  FuturesAgreementRecord,
  InferType<typeof futuresTradingRegulatoryFormSchema>
> = {
  toForm: (_, formData) => {
    // no-op
    return formData
  },
  toModel: (formData, futuresAgreementRecord) => {
    const { affiliationDetail, cftcAffiliationType, hasCftcAffiliation } =
      formData

    if (hasCftcAffiliation) {
      const isRegistered =
        cftcAffiliationType === REGULATORY_BODY_OR_EXCHANGE_MEMBER
      const isRegistrationExempt = cftcAffiliationType === EXEMPTION
      const hasFuturesIndustryAffiliation = cftcAffiliationType === FCM_OR_IB

      futuresAgreementRecord.isRegistered = isRegistered
      futuresAgreementRecord.registrationMember = isRegistered
        ? affiliationDetail
        : ""
      futuresAgreementRecord.isRegistrationExempt = isRegistrationExempt
      futuresAgreementRecord.registrationExemption = isRegistrationExempt
        ? affiliationDetail
        : ""
      futuresAgreementRecord.hasFuturesIndustryAffiliation =
        hasFuturesIndustryAffiliation
      futuresAgreementRecord.futuresIndustryFirm = hasFuturesIndustryAffiliation
        ? "-"
        : ""
    }

    return futuresAgreementRecord
  },
}
